import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import {
  makeStyles, Button, Box, Divider, CircularProgress, TextField,
} from '@material-ui/core';
import { DateRange } from '@material-ui/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  UnauthenticatedTemplate, AuthenticatedTemplate, useMsal, useAccount,
} from '@azure/msal-react';
import Alert from '@material-ui/lab/Alert';
import AppContext from '../../state/AppContext';
import { loginRequest } from '../../config/authConfig';
import SelectedTimeSlotDetails from '../Common/SelectedTimeSlotDetails';
import { fetchStaticToken, swallowMsalErrors } from '../../helpers/authentication';
import api from '../../config/api';
import LinearProgressValidatingCustomer from '../Common/LinearProgressValidatingCustomer';
import clientCategoryState, { clientTypeState } from '../../state';
import { isLoadingPriceState } from '../../state/apiRequestStatus';
import {
  selectedClinicIdState,
  selectedMedicalFileIdState,
  selectedServiceIdState,
  selectedSubserviceIdState,
  selectedTimeSlotState,
  selectedTreatmentIdState,
} from '../../state/selected';
import {
  jwtTokenState,
  reservationState,
  tarificationIdState,
  validatedCustomerState,
} from '../../state/apiData';

const useStyles = makeStyles(() => ({
  container: {},
}));

const MapAppointmentConfirmation = () => {
  const intl = useIntl();
  const classes = useStyles();
  const {
    browserFingerprint,
    setIsAppointmentConfirmed,
    customerNotValid,
    appointmentToModify,
    isFirstAppointment,
    setAppointmentDeletionFailed,
    isFollowUp,
  } = useContext(AppContext);

  const {
    instance,
    accounts,
    inProgress,
  } = useMsal();

  const containerElem = useRef(null);

  const account = useAccount(accounts[0] || {});

  const jwtToken = useRecoilValue(jwtTokenState);
  const tarificationId = useRecoilValue(tarificationIdState);
  const validatedCustomer = useRecoilValue(validatedCustomerState);
  const selectedTreatmentId = useRecoilValue(selectedTreatmentIdState);
  const selectedServiceId = useRecoilValue(selectedServiceIdState);
  const selectedSubserviceId = useRecoilValue(selectedSubserviceIdState);
  const selectedMedicalFileId = useRecoilValue(selectedMedicalFileIdState);
  const selectedClinicId = useRecoilValue(selectedClinicIdState);
  const clientType = useRecoilValue(clientTypeState);
  const clientCategory = useRecoilValue(clientCategoryState);
  const isLoadingPrice = useRecoilValue(isLoadingPriceState);
  const [selectedTimeSlot, setSelectedTimeSlot] = useRecoilState(selectedTimeSlotState);
  const [reservation, setReservation] = useRecoilState(reservationState);

  const [isReserving, setIsReserving] = useState(false);
  const [errorReservation, setErrorReservation] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [errorBooking, setErrorBooking] = useState(false);
  const [commentsValue, setCommentsValue] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const deleteAppt = () => fetchStaticToken({
    url: api.futureAppointment,
    method: 'DELETE',
    body: {
      email: validatedCustomer.email,
      customerID: validatedCustomer.customerID,
      jwt: jwtToken,
      appointmentID: appointmentToModify.id,
    },
  });

  const onLogin = async () => {
    instance.loginPopup(loginRequest(intl.locale)).catch(swallowMsalErrors);

    setErrorReservation(false);
    setIsReserving(true);

    const responseReservation = await fetchStaticToken({
      url: api.reservations,
      method: 'POST',
      body: {
        date: selectedTimeSlot.fromDayjs.format('YYYY-MM-DD'),
        heureIN: selectedTimeSlot.fromDayjs.format('HH:mm'),
        therapeuteID: selectedTimeSlot.employeeID,
        createurID: browserFingerprint,
        tarificationID: tarificationId,
      },
    });

    if (responseReservation.status === 200 || responseReservation.status === 201) {
      const loginReservation = await responseReservation.json();
      setReservation(loginReservation);
    }

    return setIsReserving(false);
  };

  const onConfirmAppt = async () => {
    if (!isFollowUp && commentsValue.length < 1) {
      return setIsSubmitted(true);
    }

    setErrorBooking(false);
    setIsBooking(true);
    setAppointmentDeletionFailed(false);

    if (appointmentToModify && isFirstAppointment) {
      const responseFutureAppointmentDelete = await deleteAppt();

      if (responseFutureAppointmentDelete.status !== 200) {
        return setAppointmentDeletionFailed(true);
      }
    }

    if (window && window.gtag) {
      window.gtag('event', 'select_content', {
        content_name: 'Confirm Appointment',
        content_lang: intl.locale,
      });
    }

    const responseBooking = await fetchStaticToken({
      url: api.appointment,
      method: 'POST',
      body: {
        customerID: validatedCustomer.customerID,
        therapistID: selectedTimeSlot.employeeID,
        disciplineID: selectedServiceId,
        subdisciplineID: selectedSubserviceId,
        treatmentID: selectedTreatmentId,
        clinicID: selectedClinicId,
        date: selectedTimeSlot.fromDayjs.format('YYYY-MM-DD'),
        time: selectedTimeSlot.fromDayjs.format('HH:mm:ss'),
        email: account.idTokenClaims.emails[0],
        jwt: jwtToken,
        comments: commentsValue,
        reservationID: reservation ? reservation.id : null,
        dmedID: selectedMedicalFileId,
        client_type: clientType,
        client_category: clientCategory,
      },
    });

    if (responseBooking.status === 200 || responseBooking.status === 201) {
      if (appointmentToModify && !isFirstAppointment) {
        const responseFutureAppointmentDelete = await deleteAppt();

        if (responseFutureAppointmentDelete.status !== 200) {
          setAppointmentDeletionFailed(true);
        }
      }
      setIsAppointmentConfirmed(true);
      return setIsBooking(false);
    }

    setErrorBooking(true);
    return setIsBooking(false);
  };

  useEffect(() => {
    if (containerElem) {
      containerElem.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, []);

  const renderConfirmBtn = () => (
    <>
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={onConfirmAppt}
        disabled={isBooking || isLoadingPrice}
      >
        {isBooking || isLoadingPrice
          ? <CircularProgress size={20} />
          : <FormattedMessage id="SelectedTimeSlotDetails.confirm" />}
      </Button>
      {errorBooking && !isFirstAppointment
        && (
          <Box mt={3}>
            <Alert severity="error">
              <FormattedMessage id="MapAppointmentConfirmation.errorBooking" />
            </Alert>
          </Box>
        )}
      {errorBooking && isFirstAppointment
        && (
          <Box mt={3}>
            <Alert severity="error">
              <FormattedMessage id="MapAppointmentConfirmation.errorBookingFirstAppointment" />
            </Alert>
          </Box>
        )}
    </>
  );

  return (
    <div className={classes.container} ref={containerElem}>
      <Box mb={3}>
        <Button
          startIcon={<DateRange />}
          color="primary"
          onClick={() => {
            if (window && window.gtag) {
              window.gtag('event', 'select_content', {
                content_name: 'Back to / Calendar',
                content_lang: intl.locale,
              });
            }

            setSelectedTimeSlot(null);
          }}
        >
          <FormattedMessage id="SelectedTimeSlotDetails.backToCalendarBtn" />
        </Button>
      </Box>

      <Divider />

      <Box mt={4}>
        <SelectedTimeSlotDetails />
      </Box>

      <AuthenticatedTemplate>
        {!isFollowUp && validatedCustomer
          && (
            <Box mb={3}>
              <TextField
                name="comments"
                label={(<FormattedMessage id="SignupForm.comments" />)}
                helperText={isSubmitted && commentsValue.length < 1 && (
                  <FormattedMessage id="SignupForm.errorRequired" />)}
                value={commentsValue}
                onChange={(e) => {
                  setCommentsValue(e.target.value);
                }}
                error={isSubmitted && commentsValue.length < 1}
                required
                variant="outlined"
                multiline
                rows={4}
                fullWidth
                inputProps={{
                  maxLength: 250,
                }}
              />
            </Box>
          )}
      </AuthenticatedTemplate>

      <Box mb={3}>
        <UnauthenticatedTemplate>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={onLogin}
            disabled={isReserving || inProgress === 'login'}
          >
            {isReserving || inProgress === 'login'
              ? <CircularProgress size={20} />
              : <FormattedMessage id="SelectedTimeSlotDetails.login" />}
          </Button>
          {errorReservation
            && (
              <Box mt={3}>
                <Alert severity="error">
                  <FormattedMessage id="MapAppointmentConfirmation.errorReservation" />
                </Alert>
              </Box>
            )}
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>
          {validatedCustomer
            && (
              renderConfirmBtn()
            )}

          <LinearProgressValidatingCustomer />
          {customerNotValid
            && (
              <Alert severity="error">
                <FormattedMessage id="MapAppointmentConfirmation.errorAccountNotValid" />
              </Alert>
            )}
        </AuthenticatedTemplate>
      </Box>
    </div>
  );
};

MapAppointmentConfirmation.propTypes = {};

export default MapAppointmentConfirmation;
